import type { AdvertisersCategories } from '@metapic-apps/shared'
import type { Ref } from 'vue'
import { ref, watchEffect } from 'vue'

import {
  normalizeAdvertiserItems,
  normalizeAdvertisersCategories,
} from '@/lib/adapters/advertisers.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { makeQueryString } from '@/lib/helpers/query.helpers'
import { request } from '@/lib/services'
import type { AdvertiserListItemType } from '@/lib/types/advertiser.types'
import { useErrorStore } from '@/stores/error'

export const useGetAdvertiserListRequest = (
  advertiserGroupId: Ref<number | undefined>,
  query: Ref<string>,
) => {
  const data = ref<AdvertiserListItemType[]>([])
  const totalAmount = ref(0)
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchAdvertisers = async (queryString: string | null) => {
    try {
      data.value = []
      isLoading.value = true
      error.value = null
      const response = await request.get(`/v2/stores${queryString ? `?${queryString}` : ''}`)
      totalAmount.value = response.data.meta.total
      data.value = normalizeAdvertiserItems(response.data.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      setTimeout(() => (isLoading.value = false), 200)
    }
  }

  watchEffect(() => {
    if (advertiserGroupId.value !== -1) {
      const queryString = makeQueryString({
        advertiserGroupId: advertiserGroupId.value,
        query: query.value,
        sort: 'asc',
        size: '20',
      })
      void fetchAdvertisers(queryString)
    }
  })

  return {
    data,
    isLoading,
    totalAmount,
    error,
  }
}

export const useGetAdvertisersCategoriesListRequest = () => {
  const data = ref<AdvertisersCategories[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const getCategories = async () => {
    try {
      data.value = []
      isLoading.value = true
      error.value = null
      const response = await request.get('/v2/store-categories')
      data.value = normalizeAdvertisersCategories(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      setTimeout(() => (isLoading.value = false), 200)
    }
  }

  watchEffect(() => void getCategories())

  return {
    getCategories,
    data,
    isLoading,
    error,
  }
}
